import { WalkthroughStep } from "../../../common/components/walkthroughV3/useWalkthrough";

export const securitySettingsSteps: WalkthroughStep[] = [
  // ── Overview ────────────────────────────────────────────────────────────────
  {
    title: {
      en: "Security Settings",
      tl: "Mga Setting ng Seguridad",
    },
    caption: {
      en: "This page lets you manage your **personal profile** and **account security** — including your profile picture, personal details, login credentials, and Two-Factor Authentication.",
      tl: "Ang pahinang ito ay nagpapahintulot sa iyo na pamahalaan ang iyong **personal na profile** at **seguridad ng account** — kasama ang iyong profile picture, personal na detalye, mga kredensyal sa pag-login, at Two-Factor Authentication.",
    },
    highlights: [{ id: "security-menu-security-settings" }],
    group: "Overview",
    captionPosition: "right",
  },

  // ── Personal Information ─────────────────────────────────────────────────
  {
    title: {
      en: "Profile Picture",
      tl: "Larawan ng Profile",
    },
    caption: {
      en: "Click the **red edit icon** on your profile picture to upload a new photo.",
      tl: "I-click ang **pulang edit icon** sa iyong profile picture para mag-upload ng bagong larawan.",
    },
    highlights: [{ id: "edit-profile-picture-button" }],
    group: "Personal Information",
    isButtonOrSearchBar: true,
    captionPosition: "bottom",
    subCaption: [
      {
        title: {
          en: "What to expect:",
          tl: "Ano ang aasahan:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Accepted formats:** JPG, JPEG, PNG only.",
              tl: "**Tinatanggap na format:** JPG, JPEG, PNG lamang.",
            },
          },
          {
            subTitle: {
              en: "**Crop tool:** After selecting a photo, a crop editor lets you zoom and reposition before saving.",
              tl: "**Crop tool:** Pagkatapos pumili ng larawan, maaari kang mag-zoom at mag-reposition bago i-save.",
            },
          },
          {
            subTitle: {
              en: "**Instant update:** Your new photo appears across all Bizbox applications.",
              tl: "**Instant na update:** Ang iyong bagong larawan ay lalabas sa lahat ng Bizbox application.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Personal Information",
      tl: "Personal na Impormasyon",
    },
    caption: {
      en: "This section shows your personal details. You can view and update all your profile information here.",
      tl: "Ipinapakita ng seksyong ito ang iyong personal na detalye. Maaari kang tumingin at mag-update ng lahat ng iyong impormasyon sa profile dito.",
    },
    highlights: [{ id: "edit-profile-personal-section" }],
    group: "Personal Information",
    captionPosition: "bottom",
    subCaption: [
      {
        title: {
          en: "Fields in this section:",
          tl: "Mga field sa seksyong ito:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Name** — Last Name, First Name, Middle Name, Suffix, Prefix, Custom Name.",
              tl: "**Pangalan** — Apelyido, Unang Pangalan, Gitnang Pangalan, Suffix, Prefix, Custom Name.",
            },
          },
          {
            subTitle: {
              en: "**Birthdate** — Required. Used for identity and compliance.",
              tl: "**Kaarawan** — Kinakailangan. Ginagamit para sa pagkakakilanlan at pagsunod.",
            },
          },
          {
            subTitle: {
              en: "**Gender & Civil Status** — Optional personal details.",
              tl: "**Kasarian at Katayuang Sibil** — Opsyonal na personal na detalye.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Edit Your Details",
      tl: "I-edit ang Iyong mga Detalye",
    },
    caption: {
      en: "Click **Edit** to enter edit mode. Once done, click **Save** to apply your changes.",
      tl: "I-click ang **Edit** para pumasok sa edit mode. Kapag tapos na, i-click ang **Save** para ilapat ang iyong mga pagbabago.",
    },
    highlights: [{ id: "edit-save-profile-button" }],
    group: "Personal Information",
    isButtonOrSearchBar: true,
    captionPosition: "bottom",
    subCaption: [
      {
        title: {
          en: "Required fields:",
          tl: "Mga kinakailangang field:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**First Name** — Must not be empty.",
              tl: "**Unang Pangalan** — Hindi dapat walang laman.",
            },
          },
          {
            subTitle: {
              en: "**Last Name** — Must not be empty.",
              tl: "**Apelyido** — Hindi dapat walang laman.",
            },
          },
          {
            subTitle: {
              en: "**Birthdate** — Must be a valid date.",
              tl: "**Kaarawan** — Dapat ay wastong petsa.",
            },
          },
        ],
      },
    ],
  },

  // ── Login Information ────────────────────────────────────────────────────
  {
    title: {
      en: "Login Information",
      tl: "Impormasyon sa Pag-login",
    },
    caption: {
      en: "This section lets you update your **email address**, **mobile number**, and **password**. Each one has its own **Change** button.",
      tl: "Ang seksyong ito ay nagpapahintulot sa iyo na i-update ang iyong **email address**, **mobile number**, at **password**. Ang bawat isa ay may sariling **Change** button.",
    },
    highlights: [{ id: "edit-profile-login-section" }],
    group: "Login Information",
    captionPosition: "bottom",
    subCaption: [
      {
        title: {
          en: "What you can change:",
          tl: "Ano ang maaari mong baguhin:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Email Address** — Your primary login credential.",
              tl: "**Email Address** — Ang iyong pangunahing kredensyal sa pag-login.",
            },
          },
          {
            subTitle: {
              en: "**Mobile Number** — Used for OTP verification.",
              tl: "**Mobile Number** — Ginagamit para sa OTP verification.",
            },
          },
          {
            subTitle: {
              en: "**Password** — Your account password.",
              tl: "**Password** — Ang password ng iyong account.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "The Change Button",
      tl: "Ang Change Button",
    },
    caption: {
      en: "Each credential has a **Change** button. Clicking it starts the update process — but first, the system will ask you to **verify your identity** before any changes are made.",
      tl: "Ang bawat kredensyal ay may **Change** button. Ang pag-click dito ay magsisimula ng proseso ng pag-update — ngunit una, hihilingin ng sistema na **i-verify ang iyong pagkakakilanlan** bago gumawa ng anumang pagbabago.",
    },
    highlights: [
      { id: "edit-email-profile-button" },
      { id: "edit-mobile-profile-button" },
      { id: "edit-password-profile-button" },
    ],
    group: "Login Information",
    isButtonOrSearchBar: true,
    captionPosition: "bottom",
  },
  {
    title: {
      en: "Identity Verification",
      tl: "Pagpapatunay ng Pagkakakilanlan",
    },
    caption: {
      en: "Before making **any change** to your login information, the system will first ask you to **enter your current password** to confirm your identity. This protects your account from unauthorized changes.",
      tl: "Bago gumawa ng **anumang pagbabago** sa iyong impormasyon sa pag-login, hihilingin muna ng sistema na **ilagay ang iyong kasalukuyang password** para kumpirmahin ang iyong pagkakakilanlan. Pinoprotektahan nito ang iyong account mula sa mga hindi awtorisadong pagbabago.",
    },
    highlights: [{ id: "security-password-confirm-dialog" }],
    group: "Login Information",
    captionPosition: "right",
    skipAutoAdvance: true,
  },
  {
    title: {
      en: "Change Email Address",
      tl: "Palitan ang Email Address",
    },
    caption: {
      en: "After verifying your password, enter your **new email address** in the field that appears. An **OTP code** will be sent to your new email to confirm the change.",
      tl: "Pagkatapos ma-verify ang iyong password, ilagay ang iyong **bagong email address** sa field na lalabas. Isang **OTP code** ang ipapadala sa iyong bagong email para kumpirmahin ang pagbabago.",
    },
    highlights: [{ id: "edit-email-profile-button" }],
    group: "Login Information",
    isButtonOrSearchBar: true,
    captionPosition: "bottom",
    subCaption: [
      {
        title: {
          en: "Change Email flow:",
          tl: "Proseso ng pagpapalit ng Email:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Step 1** — Click **Change** → Enter current password to verify.",
              tl: "**Hakbang 1** — I-click ang **Change** → Ilagay ang kasalukuyang password para ma-verify.",
            },
          },
          {
            subTitle: {
              en: "**Step 2** — Type your new email address in the field.",
              tl: "**Hakbang 2** — I-type ang iyong bagong email address sa field.",
            },
          },
          {
            subTitle: {
              en: "**Step 3** — Click **Send OTP** → Enter the code sent to your new email.",
              tl: "**Hakbang 3** — I-click ang **Send OTP** → Ilagay ang code na ipinadala sa iyong bagong email.",
            },
          },
          {
            subTitle: {
              en: "**Step 4** — Click **Confirm** to complete the change.",
              tl: "**Hakbang 4** — I-click ang **Confirm** para makumpleto ang pagbabago.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Change Mobile Number",
      tl: "Palitan ang Mobile Number",
    },
    caption: {
      en: "Click **Change** next to your mobile number to update it. The process mirrors the email change — verify password first, then confirm via OTP.",
      tl: "I-click ang **Change** sa tabi ng iyong mobile number para i-update ito. Katulad ng proseso ng pagpapalit ng email — i-verify muna ang password, pagkatapos ay kumpirmahin sa pamamagitan ng OTP.",
    },
    highlights: [{ id: "edit-mobile-profile-button" }],
    group: "Login Information",
    isButtonOrSearchBar: true,
    captionPosition: "bottom",
    subCaption: [
      {
        title: {
          en: "Note:",
          tl: "Tandaan:",
        },
        subTitle: [
          {
            subTitle: {
              en: "The OTP for mobile number changes is sent to your **registered email address**, not your mobile.",
              tl: "Ang OTP para sa mga pagbabago ng mobile number ay ipinapadala sa iyong **nakalista na email address**, hindi sa iyong mobile.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Change Password",
      tl: "Palitan ang Password",
    },
    caption: {
      en: "Click **Change** to update your password. After verifying your identity, enter and confirm your new password in the fields that appear.",
      tl: "I-click ang **Change** para i-update ang iyong password. Pagkatapos ma-verify ang iyong pagkakakilanlan, ilagay at kumpirmahin ang iyong bagong password sa mga field na lalabas.",
    },
    highlights: [{ id: "edit-password-profile-button" }],
    group: "Login Information",
    isButtonOrSearchBar: true,
    captionPosition: "bottom",
    subCaption: [
      {
        title: {
          en: "Password requirements:",
          tl: "Mga kinakailangan ng password:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Minimum 8 characters** in length.",
              tl: "**Minimum 8 na character** ang haba.",
            },
          },
          {
            subTitle: {
              en: "**At least 1 uppercase** letter (A–Z).",
              tl: "**Hindi bababa sa 1 uppercase** na letra (A–Z).",
            },
          },
          {
            subTitle: {
              en: "**At least 1 lowercase** letter (a–z).",
              tl: "**Hindi bababa sa 1 lowercase** na letra (a–z).",
            },
          },
          {
            subTitle: {
              en: "**At least 1 number** (0–9).",
              tl: "**Hindi bababa sa 1 numero** (0–9).",
            },
          },
          {
            subTitle: {
              en: "**At least 1 special character** (e.g. !, @, #, $).",
              tl: "**Hindi bababa sa 1 espesyal na character** (hal. !, @, #, $).",
            },
          },
        ],
      },
    ],
  },

  // ── Security ─────────────────────────────────────────────────────────────
  {
    title: {
      en: "Two-Factor Authentication (2FA)",
      tl: "Two-Factor Authentication (2FA)",
    },
    caption: {
      en: "Toggle this switch to **enable or disable 2FA**. When enabled, you will be asked for an additional verification code during login — adding an extra layer of security to your account.",
      tl: "I-toggle ang switch na ito para **i-enable o i-disable ang 2FA**. Kapag naka-enable, hihilingin sa iyo ng karagdagang verification code sa panahon ng pag-login — nagdadagdag ng karagdagang layer ng seguridad sa iyong account.",
    },
    highlights: [{ id: "two-factor-switch" }],
    group: "Security",
    isButtonOrSearchBar: true,
    captionPosition: "top",
  },

  // ── Tips ─────────────────────────────────────────────────────────────────
  {
    title: {
      en: "Walkthrough Icon",
      tl: "Icon ng Walkthrough",
    },
    caption: {
      en: "Tip: If you accidentally close this walkthrough, just click the **💡 icon** anytime to start it again from the beginning.",
      tl: "Tip: Kung aksidenteng nasara ang walkthrough, i-click lang ang **💡 icon** anumang oras upang simulan ito muli mula sa simula.",
    },
    highlights: [{ id: "security-settings-walkthrough-start-button" }],
    group: "Tips",
    captionPosition: "bottom",
    isButtonOrSearchBar: true,
  },
];
